import Card from 'components/Card'
import { Segmented } from 'components/ui/Segmented'
import { useReplacementLeadStats } from 'hooks/providerHooks'
import { WEEKLY_LIMIT_PER_SERVICE } from 'interfaces/LeadSettings/LeadSettingTypes'
import { useState } from 'react'
import ReplacementLeadsAllTime from './ReplacementLeadsAllTime'
import ReplacementLeadsThisWeek, { WEEK_LIMIT_INFO } from './ReplacementLeadsThisWeek'

interface Props {
    providerId: number
}

type Period = 'allTime' | 'thisWeek'

const HOW_IT_READS = [
    {
        lead: 'Received',
        text: 'is every lead that came in, replacement leads included. Nothing is deducted here.',
    },
    {
        lead: 'Week limit',
        text: WEEK_LIMIT_INFO,
    },
    {
        lead: 'N/A',
        text: 'means no week limit applies to that row, which is the case for every service while the provider is on one shared account limit.',
    },
]

const howItReadsInfo = (
    <div>
        <div className="text-[13px] font-bold text-dark mb-3">How this reads</div>
        <div className="flex flex-col gap-3">
            {HOW_IT_READS.map(({ lead, text }) => (
                <div key={lead} className="max-w-[280px]">
                    <div className="text-[12px] font-medium leading-normal text-grey-800">
                        <b className="text-dark">{lead}</b> {text}
                    </div>
                </div>
            ))}
        </div>
    </div>
)

/**
 * Replacement lead overview for CS: what this provider is still owed, what was already delivered
 * this week and how that relates to their weekly limit. Read-only.
 */
const ReplacementLeadsCard = ({ providerId }: Props) => {
    const [period, setPeriod] = useState<Period>('allTime')
    const { stats, isFetching } = useReplacementLeadStats(providerId)
    const isPerService = stats?.leadSettingsType === WEEKLY_LIMIT_PER_SERVICE

    return (
        <Card
            header="Leads this period"
            icon="bar_chart"
            isLoading={isFetching}
            infoHover={howItReadsInfo}
            topRight={
                <Segmented<Period>
                    value={period}
                    options={[
                        { value: 'allTime', label: 'All time' },
                        { value: 'thisWeek', label: 'This week' },
                    ]}
                    onChange={setPeriod}
                />
            }>
            {stats &&
                (period === 'allTime' ? (
                    <ReplacementLeadsAllTime stats={stats} showOwedPerSubscription={isPerService} />
                ) : (
                    <ReplacementLeadsThisWeek stats={stats} />
                ))}
        </Card>
    )
}

export default ReplacementLeadsCard
